import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild,
  AfterViewInit, forwardRef, ContentChild, AfterContentInit, ApplicationRef } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR, ControlValueAccessor, FormControlDirective, FormControlName } from '@angular/forms';
import { PuiSelectRenderer } from './select-config';

declare var jQuery: any;
let nextId = 0;

export class PuiSelectChange {
  source: PuiSelectComponent;
  value?: any;
}

export const PUI_SELECT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => PuiSelectComponent),
  multi: true
};

@Component({
  exportAs: 'puiInput',
  selector: 'pui-select',
  providers: [PUI_SELECT_CONTROL_VALUE_ACCESSOR],
  templateUrl: 'select.component.html'
})
export class PuiSelectComponent implements AfterContentInit, AfterViewInit, OnChanges, ControlValueAccessor {
  @Input() id = `pui-select-${nextId++}`;
  @Input() label: string;
  @Input() placeholder: string;
  @Input() delimiter = ',';
  @Input() textField: string;
  @Input() valueField: string;
  @Input() searchField: string | string[];
  @Input() disabled: boolean;
  @Input() maxItems = 1;
  @Input() items: Array<any> = [];
  @Input() options: Array<any> = [];
  @Input() allowCustomText: boolean;
  @Input() customTextParser: (string: any) => any;
  @Input() renderer: PuiSelectRenderer;
  @Input() minChars = 3;

  @Output() change: EventEmitter<PuiSelectChange> = new EventEmitter<PuiSelectChange>();
  @Output() type: EventEmitter<any> = new EventEmitter<any>();
  @Output() search: EventEmitter<any> = new EventEmitter<any>();
  @Output() itemsChange: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('select') select: ElementRef;
  @ContentChild(FormControlName) controlName: FormControlDirective;
  @ContentChild(FormControlDirective) controlDirective: FormControlDirective;
  _control: FormControl = new FormControl();

  private selectize: any;

  constructor(private appRef: ApplicationRef) {}

  private onTouchedCallback: () => void = () => { };
  private onChangeCallback: (_: any) => void = () => { };

  writeValue(v: any): void {
    if (this.selectize) {
      if (!this.options) {
        // the field is initialized through the control
        this.items = v;
        this.initializeItems();
        this._control.markAsPristine();
        this._control.markAsUntouched();
      } else {
        // triggers the onChange()
        this.selectize.setValue(v instanceof Array ?
          v.map(x => x[this.valueField] !== undefined ? x[this.valueField] : x) : (v ? v.split(this.delimiter) : v));
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  ngOnChanges(changes: SimpleChanges) {
    // bind items
    if (changes['items']) {
      this.initializeItems();
    }

    // bind options
    if (changes['options']) {
      if (this.selectize && this.options) {
        this.options.forEach(i => {
          this.selectize.addOption(i);
        });
      }
    }
  }

  ngAfterContentInit() {
    if (this.controlDirective) {
      this._control = this.controlDirective.control;
    } else if (this.controlName) {
      this._control = this.controlName.control;
    }
    if (this._control.disabled) {
      this.disabled = true;
    }
  }

  ngAfterViewInit(): void {
    let element = jQuery(this.select.nativeElement);
    // https://selectize.github.io/selectize.js/
    let searchField = this.searchField ? this.searchField : this.textField;
    if (!(searchField instanceof Array)) {
      searchField = [searchField];
    }
    let sel = element.selectize({
      placeholder: this.placeholder ? this.placeholder : '',
      delimiter: this.delimiter,
      persist: true,
      maxItems: this.maxItems,
      create: this.customTextParser || this.allowCustomText,
      // sortField: {
      //     field: this.textField ? this.textField : 'text',
      //     direction: 'asc'
      // },
      items: this.items,
      searchField: searchField,
      dropdownParent: 'body',
      labelField: this.textField,
      valueField: this.valueField,
      options: this.options,
      onType: (value: any) => this.onType(value, this.type),
      onChange: (value: any) => this.onChange(value, this.change),
      load: (q: any, c: any) => this.onLoad(q, c, this.search),
      render: this.renderer ? {
        item: this.renderer.item.render,
        option: this.renderer.option ? this.renderer.option.render : this.renderer.item.render
      } : undefined
    });

    this.selectize = sel[0].selectize;

    // initializes the items from the control
    if (this._control.value && this.items.length === 0) {
      this.items = this._control.value;
    }

    this.initializeItems();
    this._control.markAsPristine();
    this._control.markAsUntouched();
  }

  private onChange(value: any, event: EventEmitter<any>) {
    this.items = this.selectize.items;
    event.emit(<PuiSelectChange>{
      source: this,
      value: this.items
    });
    this._control.markAsDirty();
    this._control.markAsTouched();
    this.onTouchedCallback();
    this.onChangeCallback(this.items);
  }

  _onSelectChange(e: any) {
    e.stopPropagation();
  }

  private onLoad(query: string, callback: any, event: EventEmitter<any>) {
    if (query.length === 0 || query.length >= this.minChars) {
      event.emit({ query: query, callback: callback });
    }
  }

  private onType(terms: any, event: EventEmitter<any>) {
    if (terms.length > 1) {
      event.emit({ terms: terms });
    }
  }

  private initializeItems(clear = true, silent = false): void {
    if (this.selectize) {
      if (clear) {
        this.selectize.clear(true);
      }
      if (this.items) {
        if (this.items.every(item => !item[this.valueField])) {
          this.selectize.setValue(this.items, silent);
        } else {
          let values: any[] = [];
          this.items.forEach(i => {
            if (i[this.valueField]) {
              // add option if not there yet
              this.selectize.addOption(i);
              values.push(i[this.valueField]);
            }
          });
          this.selectize.setValue(values, silent);
        }
        this.selectize.refreshOptions(false);
      }
    }
  }

  _onEnter(e: KeyboardEvent): void {
    // TODO: check if the dropdown is open
    e.stopPropagation();
  }
}
